// src/features/TechnicianAnalysis/PartsAnalytics.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  LocalShipping,
  Inventory,
  Warning
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { PartsInventoryService } from '../../services/partsInventoryService';

const PartsAnalytics = ({ user }) => {
  const { t } = useI18n();
  const [inventoryService] = useState(new PartsInventoryService());
  const [analytics, setAnalytics] = useState({
    totalValue: 0,
    lowStockCount: 0,
    monthlyUsage: 0,
    predictionAccuracy: 0
  });
  const [usageTrends, setUsageTrends] = useState([]);
  const [costAnalysis, setCostAnalysis] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = () => {
    const parts = inventoryService.parts;
    const usageHistory = inventoryService.usageHistory;
    
    const totalValue = inventoryService.getInventoryValue();
    const lowStockCount = parts.filter(p => p.currentStock <= p.minStock).length;
    
    // Місячне використання
    const last30Days = usageHistory.filter(usage => 
      new Date(usage.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    );
    const monthlyUsage = last30Days.reduce((sum, usage) => sum + usage.quantity, 0);

    // Аналіз трендів використання
    const trends = inventoryService.getMostUsedParts(10);
    setUsageTrends(trends);

    // Аналіз вартості
    const costAnalysis = parts.map(part => ({
      name: part.name,
      stockValue: part.currentStock * part.cost,
      monthlyCost: (part.currentStock * part.cost) / 12, // Приблизно
      turnover: part.currentStock > 0 ? 
        (inventoryService.getMostUsedParts().find(p => p.partId === part.id)?.usageCount || 0) / part.currentStock 
        : 0
    })).sort((a, b) => b.stockValue - a.stockValue);

    setCostAnalysis(costAnalysis);

    setAnalytics({
      totalValue,
      lowStockCount,
      monthlyUsage,
      predictionAccuracy: 85 // Приклад - можна розрахувати на основі історичних даних
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('parts_analytics_title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        {t('parts_analytics_subtitle')}
      </Typography>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <LocalShipping sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" color="primary.main" fontWeight="bold">
              €{analytics.totalValue.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('total_inventory_value')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Warning sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
            <Typography variant="h4" color="error.main" fontWeight="bold">
              {analytics.lowStockCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('low_stock_items')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Inventory sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" color="warning.main" fontWeight="bold">
              {analytics.monthlyUsage}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('monthly_usage')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <TrendingUp sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" color="success.main" fontWeight="bold">
              {analytics.predictionAccuracy}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('prediction_accuracy')}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Тренди використання */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('usage_trends')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('part_name')}</strong></TableCell>
                      <TableCell><strong>{t('usage_count')}</strong></TableCell>
                      <TableCell><strong>{t('trend')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageTrends.map((part, index) => (
                      <TableRow key={part.partId}>
                        <TableCell>
                          <Typography variant="body2">
                            {part.partName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={part.usageCount} 
                            color={index < 3 ? 'error' : index < 6 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TrendingUp sx={{ 
                            color: index < 3 ? 'error.main' : 'success.main'
                          }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Аналіз вартості */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cost_analysis')}
              </Typography>
              <Stack spacing={2}>
                {costAnalysis.slice(0, 5).map((item, index) => (
                  <Paper key={item.name} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {item.name}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        €{item.stockValue.toFixed(2)}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min((item.stockValue / analytics.totalValue) * 100, 100)}
                      sx={{ height: 8, borderRadius: 4 }}
                      color={index === 0 ? 'error' : index === 1 ? 'warning' : 'primary'}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {t('monthly_cost')}: €{item.monthlyCost.toFixed(2)} • 
                      {t('turnover')}: {item.turnover.toFixed(1)}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Оптимізаційні рекомендації */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Analytics sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('optimization_recommendations')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                <Typography variant="h6" color="success.main">
                  {t('reduce_stock_levels')}
                </Typography>
                <Typography variant="body2">
                  {t('reduce_stock_desc')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                <Typography variant="h6" color="warning.main">
                  {t('reorder_critical')}
                </Typography>
                <Typography variant="body2">
                  {t('reorder_critical_desc')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                <Typography variant="h6" color="info.main">
                  {t('negotiate_suppliers')}
                </Typography>
                <Typography variant="body2">
                  {t('negotiate_suppliers_desc')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PartsAnalytics;